import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Lock,
  CheckCircle,
  User,
  Stethoscope,
  AlertCircle,
  Loader2,
  ShieldCheck,
  Receipt,
  RefreshCw,
  Pill,
  Truck,
  Gift,
  CreditCard,
  Clock,
  Package,
  Snowflake,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCart } from '@/contexts/CartContext';
import { useClinic } from '@/contexts/ClinicContext';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import storefrontCheckoutService, {
  StorefrontCheckoutRequest,
  StorefrontCheckoutResponse,
} from '@/services/storefrontCheckoutService';
import { processPayment } from '@/services/paymentService';
import {
  BinInformation,
  FormState,
  ShippingCarrier,
  ShippingOption,
  ShippingSelection,
} from '@/lib/types';

import { prescriptionService } from '@/services/prescriptionService';
import { getProductsForPatient } from '@/services/products.service';
import {
  shippingService,
  cartItemsToShippingLines,
  type PackageDimensions,
} from '@/services/shippingService';
import { StripePaymentForm } from '@/components/payments/StripePaymentForm';
import { getStripeInstance } from '@/lib/stripe-loader';
import type { ApiError } from '@/services/api';
import { clinicPaymentService } from '@/services/clinicPaymentService';
import { STRIPE_ONBOARDING_TOAST } from '@/services/paymentService';
import { checkSyringeNeeded } from '@/components/clinic/PrescriptionFormSheet/syringeCheck';
import { medicationUnits } from '@/components/clinic/PrescriptionFormSheet/RxPadForm/constants';
import SyringeWarningDialog from '@/components/clinic/PrescriptionFormSheet/SyringeWarningDialog';
import AllergyWarningDialog, { type AllergyWarning } from '@/components/clinic/PrescriptionFormSheet/AllergyWarningDialog';
import { checkAllergyInteractions } from '@/services/products.service';
import ReferringProviderSelect from '@/components/commissions/ReferringProviderSelect';

// Checkout Steps
enum CheckoutStep {
  REVIEW = 'review',
  PAYMENT = 'payment',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  ERROR = 'error',
}

// Success Component
const CheckoutSuccess: React.FC<{
  prescriptionId: string;
  onContinue: () => void;
  isHoldPlaced?: boolean;
  isStaff?: boolean;
  isPaymentDeferred?: boolean;
}> = ({ prescriptionId, onContinue, isHoldPlaced, isStaff, isPaymentDeferred }) => {
  return (
    <Card className='border-slate-200'>
      <CardContent className='py-12'>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className='space-y-6 text-center'
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full ${
              isPaymentDeferred ? 'bg-amber-100' : isHoldPlaced ? 'bg-blue-100' : 'bg-emerald-100'
            }`}
          >
            {isPaymentDeferred ? (
              <Clock className='h-10 w-10 text-amber-600' />
            ) : isHoldPlaced ? (
              <Lock className='h-10 w-10 text-blue-600' />
            ) : (
              <CheckCircle className='h-10 w-10 text-emerald-600' />
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className='mb-2 text-2xl font-bold text-slate-900'>
              {isPaymentDeferred
                ? 'Order Submitted — Payment Deferred'
                : isHoldPlaced
                  ? 'Authorization Hold Placed'
                  : 'Payment Successful!'}
            </h2>
            <p className='mb-4 text-slate-600'>
              {isPaymentDeferred
                ? 'The order has been created and an intake form will be sent to the patient. Payment will be collected at the end of the intake process.'
                : isHoldPlaced
                  ? isStaff
                    ? 'The card has been authorized. The charge will only be captured after a provider reviews and verifies the treatment plan.'
                    : 'Your card has been authorized but not charged. You will only be charged once your personalized treatment plan is confirmed by a provider.'
                  : 'Your prescription has been submitted and payment processed successfully.'}
            </p>

            {/* Staff: show order status badge */}
            {isPaymentDeferred && (
              <div className='mb-4 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-4 py-1.5'>
                <CreditCard className='h-3.5 w-3.5 text-amber-600' />
                <span className='text-sm font-medium text-amber-700'>
                  Payment: To be collected at intake
                </span>
              </div>
            )}
            {isHoldPlaced && !isPaymentDeferred && isStaff && (
              <div className='mb-4 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-4 py-1.5'>
                <div className='h-2 w-2 rounded-full bg-amber-500' />
                <span className='text-sm font-medium text-amber-700'>
                  Order Status: Pending Provider Review
                </span>
              </div>
            )}

            <div className='inline-block rounded-xl border border-slate-100 bg-slate-50 p-4'>
              <div className='flex items-center gap-2 text-slate-700'>
                <Receipt className='h-4 w-4' />
                <span className='text-sm'>Order ID:</span>
                <span className='font-semibold'>{prescriptionId}</span>
              </div>
            </div>

            {isHoldPlaced && (
              <p className='mt-4 text-sm text-blue-600'>
                {isStaff
                  ? 'If no provider action is taken within 7 days, the hold will expire automatically and the card will not be charged.'
                  : "If we don't confirm within 7 days, the hold will expire and you won't be charged."}
              </p>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
            <Button
              onClick={onContinue}
              className='bg-[rgb(35,31,32)] px-8 text-white hover:bg-[rgb(35,31,32)]/90'
            >
              View Prescriptions
            </Button>
          </motion.div>
        </motion.div>
      </CardContent>
    </Card>
  );
};

// Main Checkout Component
const Checkout: React.FC = () => {
  const {
    items,
    cartCount,
    cartTotal,
    hasSubscriptions,
    subscription,
    priority,
    selectedPatient,
    selectedPractitioner,
    canCheckout,
    clearCart,
    setItemDiscount,
  } = useCart();
  const { clinic, isClinicStaff, isPractitioner, isAdmin } = useClinic();
  const stripeAccountId = clinic?.paymentAccount?.stripe_account_id;
  const stripeFullyOnboarded =
    !!stripeAccountId &&
    (clinic?.paymentAccount?.stripe_charges_enabled === true);
  const navigate = useNavigate();
  const [onboardingLinkLoading, setOnboardingLinkLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<CheckoutStep>(CheckoutStep.REVIEW);
  const [checkoutResponse, setCheckoutResponse] = useState<StorefrontCheckoutResponse | null>(null);
  const [error, setError] = useState<string>('');

  // Referrer attribution (guided checkout only, staff view only).
  const [referringUserId, setReferringUserId] = useState<string | null>(null);
  const [commissionRateBps, setCommissionRateBps] = useState<number | null>(null);

  // ============================================
  // SHIPPING STATE
  // ============================================
  /** Available shipping carriers fetched from API */
  const [carriers, setCarriers] = useState<ShippingCarrier[]>([]);
  /** Currently selected carrier ID */
  const [selectedCarrierId, setSelectedCarrierId] = useState<string>('');
  /** Available delivery options for selected carrier */
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  /** Currently selected shipping option ID */
  const [selectedShippingOptionId, setSelectedShippingOptionId] = useState<string>('');
  /** Loading state for carriers fetch */
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
  /** Loading state for shipping options fetch */
  const [isLoadingShippingOptions, setIsLoadingShippingOptions] = useState(false);
  /** Whether patient qualifies for free shipment */
  const [isFreeShipment, setIsFreeShipment] = useState(false);
  /** Type of free shipment: 'global' or 'clinic_specific' */
  const [freeShipmentType, setFreeShipmentType] = useState<string | null>(null);
  /** Patient's state for free shipment display */
  const [patientState, setPatientState] = useState<string | null>(null);
  /** Package dimensions used for shipping rate calculation */
  const [packageDimensions, setPackageDimensions] = useState<PackageDimensions | null>(null);
  /** Current shipping cost in dollars */
  const [shippingCostDollars, setShippingCostDollars] = useState(0);
  /** Original shipping cost before free shipment discount */
  const [shippingOriginalCostDollars, setShippingOriginalCostDollars] = useState(0);
  /** Error message for shipping options (shown inline) */
  const [shippingError, setShippingError] = useState<string | null>(null);
  /** Whether this order requires cold chain (overnight/express) shipping */
  const [requiresColdChain, setRequiresColdChain] = useState(false);
  /** Whether this checkout routes to external fulfillment (e.g. OptioRx) */
  const [isExternalFulfillment, setIsExternalFulfillment] = useState(false);

  const [totalDiscount, setTotalDiscount] = useState(0);
  const [discountInput, setDiscountInput] = useState('');

  // Syringe warning state
  const [showSyringeWarning, setShowSyringeWarning] = useState(false);
  const [syringeWarningMedications, setSyringeWarningMedications] = useState<string[]>([]);
  const [syringeWarningAcknowledged, setSyringeWarningAcknowledged] = useState(false);
  const [pendingSyringeAction, setPendingSyringeAction] = useState<'payment' | 'skip' | null>(null);

  // Allergy warning state
  const [showAllergyWarning, setShowAllergyWarning] = useState(false);
  const [allergyWarnings, setAllergyWarnings] = useState<AllergyWarning[]>([]);
  const [allergyWarningsAcknowledged, setAllergyWarningsAcknowledged] = useState(false);
  const [pendingAllergyAction, setPendingAllergyAction] = useState<'payment' | 'skip' | null>(null);

  const cartSubtotalBeforeDiscount = items.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
  const maxDiscount = items.reduce((sum, item) => {
    const margin = Math.max(0, item.price - (item.base_price || 0));
    return sum + margin * (item.quantity || 1);
  }, 0);
  const checkoutDiscountError = Math.round(totalDiscount * 100) > Math.round(maxDiscount * 100);
  const cartTotalAfterDiscount = cartSubtotalBeforeDiscount - totalDiscount;

  const hasShipping = isExternalFulfillment || (!!selectedCarrierId && !!selectedShippingOptionId);
  const totalWithShipping = cartTotalAfterDiscount + shippingCostDollars;
  const isGuidedCheckout = items.some(item => item.display_mode === 'protocol_showcase');
  const showPractitionerDropdown = items.length > 0 && !isGuidedCheckout;
  const guidedOptionId = isGuidedCheckout ? items[0]?.clinic_program_option_id : undefined;

  useEffect(() => {
    if (!canCheckout && items.length === 0) {
      navigate('/storefront/cart');
    }
  }, [canCheckout, items.length, navigate]);

  /**
   * Gets the current shipping selection object
   */
  const getShippingSelection = (): ShippingSelection | null => {
    if (!selectedCarrierId || !selectedShippingOptionId) return null;

    const carrier = carriers.find(c => c.id === selectedCarrierId);
    const option = shippingOptions.find(o => o.id === selectedShippingOptionId);

    if (!carrier || !option) return null;

    return {
      carrierId: carrier.id,
      carrierName: carrier.name,
      optionId: option.id,
      optionName: option.name,
      cost: option.totalCost,
      originalCost: option.originalCost,
      isFreeShipment,
      freeShipmentType,
    };
  };

  /**
   * Fetches available shipping carriers from the API
   */
  const fetchCarriers = async () => {
    if (!clinic?.id) return;

    setIsLoadingCarriers(true);
    try {
      const result = await shippingService.getCarriers(clinic.id);
      if (result.success && result.data) {
        setCarriers(result.data);
      } else {
        setShippingError(result.error || 'Failed to load shipping carriers');
      }
    } catch (error) {
      console.error('Error fetching carriers:', error);
      setShippingError('Failed to load shipping carriers');
    } finally {
      setIsLoadingCarriers(false);
    }
  };

  /**
   * Handles carrier selection and fetches shipping options for the selected carrier
   */
  const handleCarrierChange = async (carrierId: string) => {
    setSelectedCarrierId(carrierId);
    setSelectedShippingOptionId('');
    setShippingOptions([]);
    setIsFreeShipment(false);
    setFreeShipmentType(null);
    setShippingCostDollars(0);
    setShippingOriginalCostDollars(0);
    setShippingError(null);
    setPackageDimensions(null);
    setRequiresColdChain(false);

    if (!carrierId || !clinic?.id || !selectedPatient?.id) return;

    setIsLoadingShippingOptions(true);
    try {
      const result = await shippingService.calculateShippingCost(
        clinic.id,
        selectedPatient.id,
        carrierId,
        cartItemsToShippingLines(items)
      );

      if (result.success && result.data) {
        setShippingOptions(result.data);
        setIsFreeShipment(result.isFreeShipment);
        setFreeShipmentType(result.freeShipmentType || null);
        setPatientState(result.patientState || null);
        setPackageDimensions(result.packageDimensions ?? null);
        setRequiresColdChain(result.requiresColdChain);
        setShippingError(null);

        if (result.data.length > 0) {
          handleShippingOptionChange(result.data[0].id, result.data);
        }
      } else {
        setShippingError(
          result.error || 'Failed to calculate shipping cost. Please try a different carrier.'
        );
      }
    } catch (error) {
      console.error('Error calculating shipping cost:', error);
      setShippingError('Failed to load shipping options. Please try again.');
    } finally {
      setIsLoadingShippingOptions(false);
    }
  };

  /**
   * Handles shipping option selection and updates the shipping cost
   */
  const handleShippingOptionChange = (optionId: string, options?: ShippingOption[]) => {
    setSelectedShippingOptionId(optionId);

    const optionsToSearch = options || shippingOptions;
    const selected = optionsToSearch.find(o => o.id === optionId);

    if (selected) {
      setShippingCostDollars(selected.totalCost);
      setShippingOriginalCostDollars(selected.originalCost);
    } else {
      setShippingCostDollars(0);
      setShippingOriginalCostDollars(0);
    }
  };

  useEffect(() => {
    if (clinic?.id) {
      fetchCarriers();
    }
  }, [clinic?.id]);

  useEffect(() => {
    setSelectedCarrierId('');
    setSelectedShippingOptionId('');
    setShippingOptions([]);
    setIsFreeShipment(false);
    setFreeShipmentType(null);
    setPatientState(null);
    setPackageDimensions(null);
    setShippingCostDollars(0);
    setShippingOriginalCostDollars(0);
    setShippingError(null);
    setIsExternalFulfillment(false);

    // Check if patient routes to external fulfillment (e.g. OptioRx)
    if (clinic?.id && selectedPatient?.id) {
      getProductsForPatient(clinic.id, selectedPatient.id, { page: 1, limit: 1 })
        .then(res => setIsExternalFulfillment(!!res?.isExternalFulfillment))
        .catch(() => setIsExternalFulfillment(false));
    }
  }, [selectedPatient?.id, clinic?.id]);

  const handleNavigation = (url: string) => {
    if (isPractitioner) {
      navigate(`/practitioner-portal${url}`);
    } else {
      // Admin + clinic_staff both live at root.
      navigate(url);
    }
  };

  const handleOpenStripeOnboarding = async () => {
    if (!clinic?.id || onboardingLinkLoading) return;
    setOnboardingLinkLoading(true);
    try {
      let result = await clinicPaymentService.refreshStripeOnboardingLink(clinic.id);
      if (!result?.onboardingUrl) {
        result = await clinicPaymentService.startStripeOnboarding(clinic.id);
      }
      if (result?.onboardingUrl) {
        window.open(result.onboardingUrl, '_blank');
      } else {
        toast({
          title: 'Could not get onboarding link',
          description: 'Please try again or complete setup in Settings → Payments.',
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Could not get onboarding link',
        description: 'Please try again or complete setup in Settings → Payments.',
        variant: 'destructive',
      });
    } finally {
      setOnboardingLinkLoading(false);
    }
  };

  const handlePaymentError = (error: string) => {
    setError(error);
    setCurrentStep(CheckoutStep.ERROR);
  };

  const handleStripePayment = async (paymentMethodId: string) => {
    setCurrentStep(CheckoutStep.PROCESSING);

    try {
      if (checkoutDiscountError) {
        setError('Discount exceeds maximum allowed amount');
        setCurrentStep(CheckoutStep.REVIEW);
        return;
      }

      if (!clinic?.id || !selectedPatient?.id || (showPractitionerDropdown && !selectedPractitioner?.id)) {
        setError('Missing checkout configuration');
        setCurrentStep(CheckoutStep.ERROR);
        return;
      }

      if (!hasShipping) {
        toast({
          title: 'Shipping Required',
          description: 'Please select a shipping carrier and delivery option.',
          variant: 'destructive',
        });
        setCurrentStep(CheckoutStep.REVIEW);
        return;
      }

      const shippingSelection = getShippingSelection();
      const checkoutData: StorefrontCheckoutRequest = {
        clinic_id: clinic.id,
        patient_id: selectedPatient.id,
        practitioner_id: selectedPractitioner?.id,
        program_id: items[0]?.program_id,
        priority: priority,
        clinic_program_option_id: guidedOptionId,
        medicationRequests: (() => {
          let remainCents = Math.round(totalDiscount * 100);
          return items.map((item, idx) => {
            const isProtocol = item.display_mode === 'protocol_showcase';
            const lineTotal = item.price * (item.quantity || 1);
            let lineDiscountCents = 0;
            if (cartSubtotalBeforeDiscount > 0 && totalDiscount > 0) {
              if (idx === items.length - 1) {
                lineDiscountCents = remainCents;
              } else {
                lineDiscountCents = Math.round((lineTotal / cartSubtotalBeforeDiscount) * Math.round(totalDiscount * 100));
                remainCents -= lineDiscountCents;
              }
            }
            return {
              additionalContext:
                item.additionalContext || `Storefront order from program: ${item.program_name}`,
              dose: item.dose || (isProtocol ? 'As prescribed' : ''),
              medication: item.medication_name || '',
              quantity: item.quantity || 1,
              reason: item.reason || `${item.program_name} - Prescription program`,
              refills: item.refills || '0',
              sig: item.sig || (isProtocol ? 'As directed by prescriber' : 'Take as directed by prescriber'),
              unit: medicationUnits.find((u) => u.value === item.unit_of_measure)?.label || item.unit_of_measure || (isProtocol ? 'program' : 'Vial'),
              daysSupply: item.daysSupply || '30',
              flavor: item.flavor || null,
              notes: item.notes || `Selected from ${item.program_name} program`,
              doNotFill: false,
              doNotRefill: false,
              patientContactRequested: false,
              refillNeeded: false,
              product_id: isProtocol ? null : item.product_id,
              template_id: item.template_id || null,
              program_id: item.program_id,
              discount_cents: lineDiscountCents,
            };
          });
        })(),
        token: paymentMethodId,
        amount: totalWithShipping,
        fraud_session_id: 'stripe_direct_charge',
        is_subscription: hasSubscriptions,
        allergyWarningsAcknowledged,
        subscription,
        payment_method_id: paymentMethodId,
        shipping: shippingSelection
          ? {
              carrier_id: shippingSelection.carrierId,
              carrier_name: shippingSelection.carrierName,
              service_id: shippingSelection.optionId,
              service_name: shippingSelection.optionName,
              cost_cents: Math.round(shippingSelection.cost * 100),
              original_cost_cents: Math.round(shippingSelection.originalCost * 100),
              is_free_shipment: shippingSelection.isFreeShipment,
              free_shipment_type: shippingSelection.freeShipmentType,
            }
          : undefined,
        referring_user_id: guidedOptionId ? referringUserId : null,
        commission_rate_bps: guidedOptionId ? commissionRateBps : null,
      };

      const response = await storefrontCheckoutService.processStorefrontCheckout(checkoutData);
      if (response.success) {
        setCheckoutResponse(response);
        setCurrentStep(CheckoutStep.SUCCESS);

        const guidedOrderId = response.data?.guided_order_id;
        const isHoldPlaced = (response.data as Record<string, unknown>)?.payment_status === 'hold_placed';

        const holdToastDescription = hasSubscriptions
          ? `Your card has been authorized. The authorization hold will convert into a recurring ${subscription?.frequency || ''} subscription charge after provider verification.`.replace(/\s+/g, ' ')
          : 'Your card has been authorized. Charge will be captured after provider verification.';

        if (guidedOrderId) {
          setTimeout(() => {
            clearCart();
          }, 3000);
          toast({
            title: 'Authorization Hold Placed',
            description: holdToastDescription,
            variant: 'success',
          });
          navigate(`/storefront/confirmation/${guidedOrderId}`, { replace: true });
          return;
        }

        if (isHoldPlaced) {
          setTimeout(() => {
            clearCart();
          }, 3000);
          toast({
            title: 'Authorization Hold Placed',
            description: holdToastDescription,
            variant: 'success',
          });
          return;
        }

        if (isPractitioner) {
          try {
            await prescriptionService.submitPrescription(response.data!.id);
          } catch (submitError: unknown) {
            const errorMessage =
              submitError instanceof Error
                ? submitError.message
                : 'Order submission to pharmacy failed.';
            toast({
              variant: 'destructive',
              title: 'Order submission failed',
              description: errorMessage,
            });
          }
        }

        setTimeout(() => {
          clearCart();
        }, 5000);

        toast({
          title: 'Order Created Successfully!',
          description: `Order ID: ${response.data!.id}`,
          variant: 'success',
        });
        return;
      }

      const checkoutError = response.error as unknown;
      const stripeActionError =
        typeof checkoutError === 'object' && checkoutError !== null
          ? (checkoutError as {
              requires_action?: boolean;
              client_secret?: string;
              message?: string;
            })
          : null;

      if (stripeActionError?.requires_action && stripeActionError.client_secret) {
        const connectedAccountId = clinic?.paymentAccount?.stripe_account_id;
        if (!connectedAccountId) {
          throw new Error('Stripe account is not configured for this clinic');
        }

        const stripe = await getStripeInstance(connectedAccountId);
        if (!stripe) {
          throw new Error('Unable to initialize Stripe');
        }

        const { error: nextActionError } = await stripe.handleNextAction({
          clientSecret: stripeActionError.client_secret,
        });
        if (nextActionError) {
          throw new Error(nextActionError.message || 'Authentication failed');
        }

        const prescriptionId = response.data?.id || response.prescription_id;
        if (!prescriptionId) {
          throw new Error('Payment authenticated but prescription ID is missing');
        }

        const recheckResult = await processPayment({
          prescription_id: prescriptionId,
          clinic_id: clinic?.id || '',
          amount: totalWithShipping,
          payment_method_id: paymentMethodId,
          is_subscription: hasSubscriptions,
          subscription_frequency: hasSubscriptions ? subscription?.frequency : undefined,
          subscription_auto_renew: hasSubscriptions ? subscription?.autoRenew : undefined,
          shipping: shippingSelection
            ? {
                carrier_id: shippingSelection.carrierId,
                carrier_name: shippingSelection.carrierName,
                service_id: shippingSelection.optionId,
                service_name: shippingSelection.optionName,
                cost_cents: Math.round(shippingSelection.cost * 100),
                original_cost_cents: Math.round(shippingSelection.originalCost * 100),
                is_free_shipment: shippingSelection.isFreeShipment,
                free_shipment_type: shippingSelection.freeShipmentType,
              }
            : undefined,
        });

        if (!recheckResult.success) {
          throw new Error(recheckResult.error?.message || 'Checkout failed');
        }

        setCheckoutResponse({
          success: true,
          data: { id: prescriptionId },
        });
        setCurrentStep(CheckoutStep.SUCCESS);
        setTimeout(() => {
          clearCart();
        }, 5000);
        toast({
          title: 'Order Created Successfully!',
          description: `Order ID: ${prescriptionId}`,
          variant: 'success',
        });
        return;
      }

      throw new Error(
        stripeActionError?.message || response.error || response.message || 'Checkout failed'
      );
    } catch (error: unknown) {
      const apiError = error as ApiError | undefined;
      const isValidationError =
        apiError?.status === 400 && Array.isArray(apiError?.details) && apiError.details.length > 0;
      const detailsList = isValidationError
        ? (apiError!.details as Array<{ field?: string; message?: string }>)
            .map(d => (d.field ? `${d.field}: ${d.message ?? 'invalid'}` : d.message ?? ''))
            .filter(Boolean)
            .join(' • ')
        : '';
      const errorMessage = isValidationError
        ? detailsList || apiError?.message || 'Please check your entries and try again.'
        : apiError?.message ?? (error instanceof Error ? error.message : 'Checkout failed. Please try again.');
      setError(errorMessage);
      setCurrentStep(isValidationError ? CheckoutStep.REVIEW : CheckoutStep.ERROR);
      toast({
        title: isValidationError ? 'Please fix the following' : 'Checkout Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleSkipPayment = async () => {
    if (!(await runAllergyCheck('skip'))) return;
    if (!runSyringeCheck('skip')) return;
    setCurrentStep(CheckoutStep.PROCESSING);

    try {
      if (!clinic?.id || !selectedPatient?.id) {
        setError('Missing checkout configuration');
        setCurrentStep(CheckoutStep.ERROR);
        return;
      }

      if (!hasShipping) {
        toast({
          title: 'Shipping Required',
          description: 'Please select a shipping carrier and delivery option.',
          variant: 'destructive',
        });
        setCurrentStep(CheckoutStep.REVIEW);
        return;
      }

      const shippingSelection = getShippingSelection();
      const checkoutData: StorefrontCheckoutRequest = {
        clinic_id: clinic.id,
        patient_id: selectedPatient.id,
        practitioner_id: selectedPractitioner?.id,
        program_id: items[0]?.program_id,
        priority: priority,
        clinic_program_option_id: guidedOptionId,
        skip_payment: true,
        medicationRequests: (() => {
          let remainCents = Math.round(totalDiscount * 100);
          return items.map((item, idx) => {
            const isProtocol = item.display_mode === 'protocol_showcase';
            const lineTotal = item.price * (item.quantity || 1);
            let lineDiscountCents = 0;
            if (cartSubtotalBeforeDiscount > 0 && totalDiscount > 0) {
              if (idx === items.length - 1) {
                lineDiscountCents = remainCents;
              } else {
                lineDiscountCents = Math.round((lineTotal / cartSubtotalBeforeDiscount) * Math.round(totalDiscount * 100));
                remainCents -= lineDiscountCents;
              }
            }
            return {
              additionalContext:
                item.additionalContext || `Storefront order from program: ${item.program_name}`,
              dose: item.dose || (isProtocol ? 'As prescribed' : ''),
              medication: item.medication_name || '',
              quantity: item.quantity || 1,
              reason: item.reason || `${item.program_name} - Prescription program`,
              refills: item.refills || '0',
              sig: item.sig || (isProtocol ? 'As directed by prescriber' : 'Take as directed by prescriber'),
              unit: medicationUnits.find((u) => u.value === item.unit_of_measure)?.label || item.unit_of_measure || (isProtocol ? 'program' : 'Vial'),
              daysSupply: item.daysSupply || '30',
              flavor: item.flavor || null,
              notes: item.notes || `Selected from ${item.program_name} program`,
              doNotFill: false,
              doNotRefill: false,
              patientContactRequested: false,
              refillNeeded: false,
              product_id: isProtocol ? null : item.product_id,
              template_id: item.template_id || null,
              program_id: item.program_id,
              discount_cents: lineDiscountCents,
            };
          });
        })(),
        token: 'skip_payment',
        amount: totalWithShipping,
        fraud_session_id: 'skip_payment',
        is_subscription: hasSubscriptions,
        allergyWarningsAcknowledged,
        subscription,
        shipping: shippingSelection
          ? {
              carrier_id: shippingSelection.carrierId,
              carrier_name: shippingSelection.carrierName,
              service_id: shippingSelection.optionId,
              service_name: shippingSelection.optionName,
              cost_cents: Math.round(shippingSelection.cost * 100),
              original_cost_cents: Math.round(shippingSelection.originalCost * 100),
              is_free_shipment: shippingSelection.isFreeShipment,
              free_shipment_type: shippingSelection.freeShipmentType,
            }
          : undefined,
        referring_user_id: guidedOptionId ? referringUserId : null,
        commission_rate_bps: guidedOptionId ? commissionRateBps : null,
      };

      const response = await storefrontCheckoutService.processStorefrontCheckout(checkoutData);
      if (response.success) {
        setCheckoutResponse(response);
        setCurrentStep(CheckoutStep.SUCCESS);

        const guidedOrderId = response.data?.guided_order_id;

        setTimeout(() => {
          clearCart();
        }, 3000);

        toast({
          title: 'Order Submitted — Payment Deferred',
          description: 'Intake form will be sent to the patient. Payment will be collected at the end of intake.',
          variant: 'success',
        });

        if (guidedOrderId) {
          navigate(`/storefront/confirmation/${guidedOrderId}`, { replace: true });
        }
        return;
      }

      throw new Error(response.error || response.message || 'Checkout failed');
    } catch (error: unknown) {
      const apiError = error as ApiError | undefined;
      const errorMessage = apiError?.message ?? (error instanceof Error ? error.message : 'Checkout failed. Please try again.');
      setError(errorMessage);
      setCurrentStep(CheckoutStep.ERROR);
      toast({
        title: 'Checkout Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleContinue = () => {
    if (isAdmin) {
      navigate(`/prescriptions`);
    } else if (isPractitioner) {
      let url = '/practitioner-portal/prescriptions';
      if (isPractitioner) url += '?status=pending';
      navigate(url);
    } else if (isClinicStaff) {
      navigate(`/prescriptions`);
    } else {
      navigate('/');
    }
  };

  /**
   * Runs syringe check on cart items before proceeding.
   * Returns true if safe to proceed if warning was shown.
   */
  const runSyringeCheck = (action: 'payment' | 'skip'): boolean => {
    if (syringeWarningAcknowledged) return true;
    const selectedProducts = items.map(item => ({
      product_type: item.product_type || null,
      name: item.medication_name || '',
    }));
    const result = checkSyringeNeeded(selectedProducts);
    if (result.needsWarning) {
      setSyringeWarningMedications(result.injectableMedications);
      setPendingSyringeAction(action);
      setShowSyringeWarning(true);
      return false;
    }
    return true;
  };

  /**
   * Runs allergy interaction check before proceeding.
   * Returns true if safe to proceed if warning dialog was shown.
   */
  const runAllergyCheck = async (action: 'payment' | 'skip'): Promise<boolean> => {
    if (allergyWarningsAcknowledged) return true;
    if (!selectedPatient?.id) return true;

    try {
      const productIds = items.map(item => item.product_id).filter(Boolean) as string[];
      const medicationNames = items.map(item => item.medication_name).filter(Boolean) as string[];
      const result = await checkAllergyInteractions(selectedPatient.id, productIds, medicationNames);
      if (result.hasWarnings) {
        setAllergyWarnings(result.warnings);
        setPendingAllergyAction(action);
        setShowAllergyWarning(true);
        return false;
      }
    } catch (err) {
      console.error('Allergy check failed, proceeding:', err);
    }
    return true;
  };

  const handleProceedToPayment = async () => {
    if (!(await runAllergyCheck('payment'))) return;
    if (!runSyringeCheck('payment')) return;
    setCurrentStep(CheckoutStep.PAYMENT);
  };

  const handleRetry = () => {
    setError('');
    setCurrentStep(CheckoutStep.PAYMENT);
  };

  if (!canCheckout) {
    return (
      <div className='space-y-6'>
        <Card className='border-slate-200'>
          <CardContent className='py-12'>
            <div className='text-center'>
              <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-100'>
                <AlertCircle className='h-8 w-8 text-amber-600' />
              </div>
              <h2 className='mb-2 text-xl font-semibold text-slate-900'>Checkout Not Available</h2>
              <p className='mx-auto mb-6 max-w-sm text-sm text-slate-500'>
                Please ensure you have items in your cart and have selected both a patient and
                practitioner.
              </p>
              <Button
                onClick={() => handleNavigation('/storefront/cart')}
                className='bg-[rgb(35,31,32)] text-white hover:bg-[rgb(35,31,32)]/90'
              >
                <ArrowLeft className='mr-2 h-4 w-4' />
                Back to Cart
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-start gap-3'>
        <Button
          variant='outline'
          size='icon'
          onClick={() => navigate(-1)}
          className='h-9 w-9 flex-shrink-0 border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          style={{ marginTop: '4px' }}
          disabled={currentStep === CheckoutStep.PROCESSING}
        >
          <ArrowLeft className='h-4 w-4' />
        </Button>
        <div>
          <h1 className='font-montserrat text-2xl font-bold tracking-tight text-slate-900'>
            Checkout
          </h1>
          <p className='mt-1 text-sm text-slate-500'>Complete your prescription order</p>
        </div>
      </div>

      <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
        {/* Main Content */}
        <div className='lg:col-span-2'>
          <AnimatePresence mode='wait'>
            {currentStep === CheckoutStep.REVIEW && (
              <motion.div
                key='review'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className='space-y-6'
              >
                <Card className='border-slate-200'>
                  <CardHeader className='pb-4'>
                    <CardTitle className='text-base font-semibold text-slate-900'>
                      Review Order
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-5'>
                    {/* Selected Patient & Practitioner */}
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                      <div className='flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4'>
                        <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-slate-200'>
                          <User className='h-5 w-5 text-slate-600' />
                        </div>
                        <div>
                          <div className='text-xs text-slate-500'>Patient</div>
                          <div className='font-medium text-slate-900'>
                            {selectedPatient?.full_name}
                          </div>
                        </div>
                      </div>
                      {showPractitionerDropdown && (
                      <div className='flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4'>
                        <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-slate-200'>
                          <Stethoscope className='h-5 w-5 text-slate-600' />
                        </div>
                        <div>
                          <div className='text-xs text-slate-500'>Practitioner</div>
                          <div className='font-medium text-slate-900'>
                            {selectedPractitioner?.full_name}
                          </div>
                        </div>
                      </div>
                      )}
                    </div>

                    {/* Cart Items */}
                    <div className='space-y-3'>
                      <h4 className='font-medium text-slate-900'>Order Items</h4>
                      {items.map(item => (
                        <div
                          key={item.product_id}
                          className='flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4'
                        >
                          <div className='flex items-center gap-3'>
                            <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10'>
                              <Pill className='h-5 w-5 text-primary' />
                            </div>
                            <div>
                              <div className='font-medium text-slate-900'>
                                {item.medication_name}
                              </div>
                              <div className='text-sm text-slate-500'>
                                {item.dose} • Qty: {item.quantity}
                              </div>
                              <div className='text-xs text-slate-400'>{item.program_name}</div>
                            </div>
                          </div>
                          <div className='text-right'>
                            <div className='font-semibold text-slate-900'>
                              ${(item.price * (item.quantity || 1)).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Shipping Selection — hidden for external fulfillment (e.g. OptioRx) */}
                    {!isExternalFulfillment && (
                    <div className='space-y-4'>
                      <h4 className='font-medium text-slate-900'>Shipping Options</h4>

                      {isFreeShipment && patientState && (
                        <div className='flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 p-3'>
                          <Gift className='h-5 w-5 text-green-600' />
                          <div>
                            <p className='text-sm font-medium text-green-800'>
                              Free Shipping Available!
                            </p>
                            <p className='text-xs text-green-600'>
                              Patient qualifies for free shipping to {patientState} (
                              {freeShipmentType === 'global' ? 'All Clinics' : 'Clinic Specific'})
                            </p>
                          </div>
                        </div>
                      )}

                      <div className='space-y-4 rounded-xl border border-slate-200 p-4'>
                        <div className='space-y-2'>
                          <Label className='text-sm font-medium text-slate-700'>
                            Shipping Carrier
                          </Label>
                          <Select
                            value={selectedCarrierId}
                            onValueChange={handleCarrierChange}
                            disabled={isLoadingCarriers}
                          >
                            <SelectTrigger className='w-full'>
                              <SelectValue
                                placeholder={
                                  isLoadingCarriers ? 'Loading carriers...' : 'Select a carrier'
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {carriers.map(carrier => (
                                <SelectItem key={carrier.id} value={carrier.id}>
                                  <div className='flex items-center gap-2'>
                                    <Truck className='h-4 w-4 text-slate-500' />
                                    {carrier.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Cold Chain Shipping Banner */}
                        {requiresColdChain && (
                          <div className='flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3'>
                            <Snowflake className='h-5 w-5 flex-shrink-0 text-blue-600' />
                            <div>
                              <p className='text-sm font-medium text-blue-800'>
                                Cold Chain Shipping Required
                              </p>
                              <p className='text-xs text-blue-600'>
                                This order contains temperature-sensitive medication. Only overnight/express options are shown.
                              </p>
                            </div>
                          </div>
                        )}

                        {selectedCarrierId && (
                          <div className='space-y-2'>
                            <Label className='text-sm font-medium text-slate-700'>
                              Delivery Option
                            </Label>
                            {isLoadingShippingOptions ? (
                              <div className='flex items-center justify-center py-4'>
                                <RefreshCw className='h-5 w-5 animate-spin text-primary' />
                                <span className='ml-2 text-sm text-slate-500'>
                                  Loading options...
                                </span>
                              </div>
                            ) : shippingOptions.length > 0 ? (
                              <RadioGroup
                                value={selectedShippingOptionId}
                                onValueChange={value => handleShippingOptionChange(value)}
                                className='space-y-2'
                              >
                                {shippingOptions.map(option => (
                                  <div
                                    key={option.id}
                                    className={`flex cursor-pointer items-center space-x-3 rounded-lg border p-3 transition-colors ${
                                      selectedShippingOptionId === option.id
                                        ? 'border-primary bg-primary/5'
                                        : 'border-slate-200 hover:bg-slate-50'
                                    }`}
                                  >
                                    <RadioGroupItem
                                      value={option.id}
                                      id={`shipping-${option.id}`}
                                    />
                                    <Label
                                      htmlFor={`shipping-${option.id}`}
                                      className='flex flex-1 cursor-pointer items-center justify-between'
                                    >
                                      <span className='text-sm font-medium text-slate-900'>
                                        {option.name}
                                      </span>
                                      <div className='flex items-center gap-2'>
                                        {isFreeShipment && option.totalCost === 0 ? (
                                          <>
                                            <span className='text-sm text-slate-400 line-through'>
                                              ${option.originalCost.toFixed(2)}
                                            </span>
                                            <Badge
                                              variant='secondary'
                                              className='bg-green-100 text-green-700 hover:bg-green-100'
                                            >
                                              FREE
                                            </Badge>
                                          </>
                                        ) : (
                                          <span className='text-sm font-semibold text-slate-900'>
                                            ${option.totalCost.toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                    </Label>
                                  </div>
                                ))}
                              </RadioGroup>
                            ) : shippingError ? (
                              <div className='flex items-start space-x-2 rounded-lg border border-red-200 bg-red-50 p-3'>
                                <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-red-600' />
                                <span className='text-sm text-red-700'>{shippingError}</span>
                              </div>
                            ) : (
                              <p className='py-2 text-sm text-slate-500'>
                                No delivery options available for this carrier
                              </p>
                            )}
                          </div>
                        )}

                        {!hasShipping && !isLoadingCarriers && carriers.length > 0 && (
                          <div className='flex items-start space-x-2 rounded-lg border border-amber-200 bg-amber-50 p-3'>
                            <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600' />
                            <span className='text-sm text-amber-700'>
                              Please select a shipping carrier and delivery option to continue.
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {/* Referrer picker: guided + staff only */}
                    {isGuidedCheckout
                      && (isAdmin || isPractitioner || isClinicStaff)
                      && clinic?.id && (
                        <div className='rounded-xl border border-slate-100 bg-slate-50 p-4'>
                          <ReferringProviderSelect
                            clinicId={clinic.id}
                            value={{
                              referringUserId,
                              commissionRateBps,
                            }}
                            onChange={val => {
                              setReferringUserId(val.referringUserId);
                              setCommissionRateBps(val.commissionRateBps);
                            }}
                          />
                        </div>
                      )}

                    <div className={`${isGuidedCheckout ? 'flex flex-col gap-3' : ''}`}>
                      <Button
                        onClick={handleProceedToPayment}
                        className='h-11 w-full bg-primary text-white hover:bg-primary/90'
                        disabled={!hasShipping}
                      >
                        <CreditCard className='mr-2 h-4 w-4' />
                        Proceed to Payment
                      </Button>

                      {isGuidedCheckout && (
                        <Button
                          variant='outline'
                          onClick={handleSkipPayment}
                          className='h-11 w-full border-amber-300 text-amber-700 hover:bg-amber-50'
                          disabled={!hasShipping}
                        >
                          <Clock className='mr-2 h-4 w-4' />
                          Skip Payment — Collect at Intake
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {currentStep === CheckoutStep.PAYMENT && (
              <motion.div
                key='payment'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {stripeFullyOnboarded ? (
                  <StripePaymentForm
                    connectedAccountId={stripeAccountId!}
                    amountCents={Math.round(totalWithShipping * 100)}
                    onPaymentMethodCreated={handleStripePayment}
                    onError={(err: string) => handlePaymentError(err)}
                    disabled={!hasShipping}
                  />
                ) : stripeAccountId ? (
                  <Card className='border border-amber-200 bg-amber-50/50'>
                    <CardHeader>
                      <CardTitle className='flex items-center gap-2 text-base text-amber-800'>
                        <AlertCircle className='h-4 w-4' />
                        {STRIPE_ONBOARDING_TOAST.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4 text-sm text-amber-800'>
                      <p>{STRIPE_ONBOARDING_TOAST.description}</p>
                      <p className='text-amber-700'>
                        Complete Stripe Connect onboarding to accept payments for this order.
                      </p>
                      {isAdmin && (
                        <div className='flex flex-wrap gap-2'>
                          <Button
                            size='sm'
                            className='bg-amber-600 text-white hover:bg-amber-700'
                            onClick={handleOpenStripeOnboarding}
                            disabled={onboardingLinkLoading}
                          >
                            {onboardingLinkLoading ? (
                              <>
                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                Opening...
                              </>
                            ) : (
                              'Complete onboarding'
                            )}
                          </Button>
                          <Button
                            variant='outline'
                            size='sm'
                            className='border-amber-300 text-amber-800 hover:bg-amber-100'
                            onClick={() => handleNavigation('/payments?tab=settings')}
                          >
                            Go to Billing Settings
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className='border border-amber-200 bg-amber-50/50'>
                    <CardHeader>
                      <CardTitle className='flex items-center gap-2 text-base text-amber-800'>
                        <AlertCircle className='h-4 w-4' />
                        Payment setup required
                      </CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-3 text-sm text-amber-800'>
                      <p>
                        This clinic has not set up Stripe payments yet. Card payments will appear here
                        after an admin connects a Stripe account in{' '}
                        <strong>Settings → Billing</strong>.
                      </p>
                      {isAdmin && (
                        <Button
                          variant='outline'
                          size='sm'
                          className='border-amber-300 text-amber-800 hover:bg-amber-100'
                          onClick={() => handleNavigation('/payments?tab=settings')}
                        >
                          Go to Billing Settings
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            )}

            {currentStep === CheckoutStep.PROCESSING && (
              <motion.div
                key='processing'
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <Card className='border-slate-200'>
                  <CardContent className='py-16'>
                    <div className='text-center'>
                      <Loader2 className='mx-auto mb-4 h-12 w-12 animate-spin text-primary' />
                      <h2 className='mb-2 text-xl font-semibold text-slate-900'>
                        Processing Order
                      </h2>
                      <p className='text-sm text-slate-500'>
                        Please wait while we process your order...
                      </p>
                      <p className='mt-4 text-xs text-slate-400'>
                        Do not refresh or close this page
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {currentStep === CheckoutStep.SUCCESS && checkoutResponse && (
              <CheckoutSuccess
                prescriptionId={
                  checkoutResponse.data?.id || checkoutResponse.data?.prescription_id || ''
                }
                onContinue={() => handleContinue()}
                isHoldPlaced={
                  (checkoutResponse.data as Record<string, unknown>)?.payment_status === 'hold_placed'
                }
                isPaymentDeferred={
                  (checkoutResponse.data as Record<string, unknown>)?.payment_status === 'deferred'
                }
                isStaff={isAdmin || isPractitioner || isClinicStaff}
              />
            )}

            {currentStep === CheckoutStep.ERROR && (
              <motion.div
                key='error'
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <Card className='border-slate-200'>
                  <CardContent className='py-12'>
                    <div className='text-center'>
                      <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-100'>
                        <AlertCircle className='h-8 w-8 text-red-600' />
                      </div>
                      <h2 className='mb-2 text-xl font-semibold text-slate-900'>Checkout Failed</h2>
                      <p className='mb-6 text-sm text-slate-500'>{error}</p>
                      <div className='flex items-center justify-center gap-3'>
                        <Button
                          onClick={handleRetry}
                          variant='outline'
                          className='border-slate-200'
                        >
                          Try Again
                        </Button>
                        <Button
                          onClick={() => handleNavigation('/storefront/cart')}
                          className='bg-[rgb(35,31,32)] text-white hover:bg-[rgb(35,31,32)]/90'
                        >
                          Back to Cart
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Order Summary Sidebar */}
        <div className='lg:col-span-1'>
          <div className='sticky top-4'>
            <Card className='border-slate-200'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-base font-semibold text-slate-900'>
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                {/* Selected Patient & Practitioner */}
                {(selectedPatient || selectedPractitioner) && (
                  <div className='space-y-2 border-b border-slate-100 pb-4'>
                    {selectedPatient && (
                      <div className='flex items-center gap-2'>
                        <User className='h-4 w-4 text-slate-400' />
                        <span className='text-sm text-slate-600'>{selectedPatient.full_name}</span>
                      </div>
                    )}
                    {showPractitionerDropdown && selectedPractitioner && (
                      <div className='flex items-center gap-2'>
                        <Stethoscope className='h-4 w-4 text-slate-400' />
                        <span className='text-sm text-slate-600'>
                          {selectedPractitioner.full_name}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className='flex justify-between text-sm'>
                  <span className='text-slate-600'>Consulting Fees ({cartCount} items)</span>
                  <span className='font-medium text-slate-900'>${cartSubtotalBeforeDiscount.toFixed(2)}</span>
                </div>

                {/* Discount Input */}
                {maxDiscount > 0 && (
                  <div className='space-y-2'>
                    <div className='flex items-center justify-between text-sm'>
                      <span className='text-slate-600'>Discount</span>
                      <div className='flex items-center gap-1'>
                        <span className='text-slate-500'>$</span>
                        <input
                          type='text'
                          inputMode='decimal'
                          value={discountInput}
                          placeholder='0.00'
                          onKeyDown={(e) => {
                            if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                              e.preventDefault();
                            }
                            if (e.key === '.' && (e.target as HTMLInputElement).value.includes('.')) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                              setDiscountInput(raw);
                              const parsed = parseFloat(raw);
                              setTotalDiscount(isNaN(parsed) ? 0 : parsed);
                            }
                          }}
                          className={`w-24 h-8 text-right text-sm rounded border px-2 ${
                            checkoutDiscountError ? 'border-red-500' : 'border-slate-200'
                          }`}
                        />
                      </div>
                    </div>
                    <div className='flex items-center justify-between'>
                      <span className='text-[11px] text-slate-400'>Max: ${maxDiscount.toFixed(2)}</span>
                      {checkoutDiscountError && (
                        <span className='text-[11px] text-red-600 font-medium'>
                          Discount exceeds maximum
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {totalDiscount > 0 && !checkoutDiscountError && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-emerald-600'>Discount Applied</span>
                    <span className='font-medium text-emerald-600'>-${totalDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className='flex justify-between text-sm'>
                  <span className='flex items-center gap-1 text-slate-600'>
                    <Truck className='h-3.5 w-3.5' />
                    Shipping
                  </span>
                  {hasShipping ? (
                    isFreeShipment ? (
                      <div className='flex items-center gap-2'>
                        <span className='text-xs text-slate-400 line-through'>
                          ${shippingOriginalCostDollars.toFixed(2)}
                        </span>
                        <span className='font-medium text-emerald-600'>Free</span>
                      </div>
                    ) : (
                      <span className='font-medium text-slate-900'>
                        ${shippingCostDollars.toFixed(2)}
                      </span>
                    )
                  ) : (
                    <span className='text-xs text-slate-400'>Select shipping</span>
                  )}
                </div>
                <div className='flex justify-between text-sm'>
                  <span className='text-slate-600'>Processing Fee</span>
                  <span className='font-medium text-emerald-600'>Free</span>
                </div>

                <Separator className='bg-slate-100' />

                <div className='flex justify-between'>
                  <span className='font-semibold text-slate-900'>Total Consulting Fees</span>
                  <span className='text-xl font-bold text-slate-900'>
                    ${totalWithShipping.toFixed(2)}
                  </span>
                </div>

                {isGuidedCheckout && (
                  <div className='rounded-xl border border-blue-200 bg-blue-50 p-3'>
                    <div className='flex items-center gap-2 text-blue-700'>
                      <Lock className='h-4 w-4' />
                      <span className='text-sm font-medium'>Authorization Hold</span>
                    </div>
                    <p className='mt-1 text-xs text-blue-600'>
                      {isAdmin || isPractitioner || isClinicStaff
                        ? 'The card will be authorized but not charged until a provider reviews and verifies the treatment plan. Alternatively, you can skip payment and collect it at the end of intake.'
                        : 'You will only be charged once your personalized treatment plan is confirmed. If not confirmed within 7 days, the hold expires and you won\'t be charged.'}
                    </p>
                  </div>
                )}

                {/* Security Notice */}
                <div className='rounded-xl border border-primary/10 bg-primary/5 p-3'>
                  <div className='flex items-center gap-2 text-primary'>
                    <ShieldCheck className='h-4 w-4' />
                    <span className='text-sm font-medium'>Secure Checkout</span>
                  </div>
                  <p className='mt-1 text-xs text-primary/70'>
                    Your payment and personal information are protected with bank-level security.
                  </p>
                </div>

                {/* Checkout Progress */}
                <div className='space-y-2'>
                  <div className='text-sm font-medium text-slate-700'>Checkout Progress</div>
                  <div className='flex items-center gap-2'>
                    <div
                      className={`h-2.5 w-2.5 rounded-full ${
                        currentStep === CheckoutStep.REVIEW ? 'bg-primary' : 'bg-emerald-500'
                      }`}
                    />
                    <span className='text-xs text-slate-600'>Review</span>
                    <div
                      className={`h-2.5 w-2.5 rounded-full ${
                        currentStep === CheckoutStep.PAYMENT
                          ? 'bg-primary'
                          : currentStep === CheckoutStep.PROCESSING ||
                              currentStep === CheckoutStep.SUCCESS
                            ? 'bg-emerald-500'
                            : 'bg-slate-200'
                      }`}
                    />
                    <span className='text-xs text-slate-600'>Payment</span>
                    <div
                      className={`h-2.5 w-2.5 rounded-full ${
                        currentStep === CheckoutStep.SUCCESS ? 'bg-emerald-500' : 'bg-slate-200'
                      }`}
                    />
                    <span className='text-xs text-slate-600'>Complete</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <SyringeWarningDialog
        open={showSyringeWarning}
        onOpenChange={setShowSyringeWarning}
        injectableMedications={syringeWarningMedications}
        onGoBack={() => {
          setShowSyringeWarning(false);
          setPendingSyringeAction(null);
        }}
        onAcknowledge={() => {
          setSyringeWarningAcknowledged(true);
          setShowSyringeWarning(false);
          if (pendingSyringeAction === 'payment') {
            setCurrentStep(CheckoutStep.PAYMENT);
          } else if (pendingSyringeAction === 'skip') {
            setPendingSyringeAction(null);
            handleSkipPayment();
          }
          setPendingSyringeAction(null);
        }}
      />

      <AllergyWarningDialog
        open={showAllergyWarning}
        onOpenChange={setShowAllergyWarning}
        warnings={allergyWarnings}
        patientName={selectedPatient?.full_name}
        patientAllergies={selectedPatient?.medication_allergies}
        onGoBack={() => {
          setShowAllergyWarning(false);
          setPendingAllergyAction(null);
        }}
        onAcknowledge={() => {
          setAllergyWarningsAcknowledged(true);
          setShowAllergyWarning(false);
          if (pendingAllergyAction === 'payment') {
            if (!runSyringeCheck('payment')) {
              setPendingAllergyAction(null);
              return;
            }
            setCurrentStep(CheckoutStep.PAYMENT);
          } else if (pendingAllergyAction === 'skip') {
            setPendingAllergyAction(null);
            handleSkipPayment();
            return;
          }
          setPendingAllergyAction(null);
        }}
      />
    </div>
  );
};

export default Checkout;
